export type CaseStudy = {
  slug: string
  path: string
  client: string
  title: string
  seoTitle: string
  seoDescription: string
  excerpt: string
  location: string
  year: string
  industry: string
  liveUrl: string
  portfolioName: string
  challenge: string
  approach: string[]
  outcome: string
  lab?: boolean
}

export const caseStudies: CaseStudy[] = [
  {
    slug: 'westview-cleaning',
    path: '/work/westview-cleaning',
    client: 'WestView Cleaning Services',
    title: 'A service-area site for a Lower Mainland cleaning company',
    seoTitle: 'WestView Cleaning Services Case Study | Elementary Creative',
    seoDescription:
      'Case study: marketing site for WestView Cleaning Services, a cleaning company serving Vancouver and the Lower Mainland.',
    excerpt: 'Service pages, local proof, and a clear path to book a clean.',
    location: 'Vancouver & Lower Mainland, BC',
    year: '2026',
    industry: 'Residential & commercial cleaning',
    liveUrl: 'https://www.westviewcleaningservices.ca',
    portfolioName: 'WestView Cleaning Services',
    challenge:
      'WestView needed a public site that explained residential and commercial cleaning for Vancouver and the Lower Mainland - without looking like a template directory listing.',
    approach: [
      'Mapped services and neighbourhoods so visitors can see coverage without a long FAQ.',
      'Built a fast marketing site with clear calls to request a quote.',
      'Kept photography and copy specific to the company, not stock “sparkling kitchen” filler.',
    ],
    outcome:
      'The live site is the company homepage: services, coverage, and contact in one place. Visit westviewcleaningservices.ca for the current launch.',
  },
  {
    slug: 'lindsay-grimley',
    path: '/work/lindsay-grimley',
    client: 'Lindsay Grimley',
    title: 'A campaign site that reads as a person, not a party template',
    seoTitle: 'Lindsay Grimley Campaign Site Case Study | Elementary Creative',
    seoDescription:
      'Case study: election campaign website for Lindsay Grimley, a Cobourg city council campaign.',
    excerpt: 'Bio, issues, and a donate/volunteer path without looking like a stock political theme.',
    location: 'Cobourg, ON',
    year: '2026',
    industry: 'Municipal campaign',
    liveUrl: 'https://lindsaygrimley.ca/',
    portfolioName: 'Lindsay Grimley',
    challenge:
      'A city-council campaign needs to introduce a candidate quickly - who they are, what they stand for, and how to help - without the visual language of a national party site.',
    approach: [
      'Led with the candidate and the town, not a generic “vote for change” banner.',
      'Kept pages short enough to share in a text: about, issues, and contact.',
      'Launched on a production domain the campaign could put on signs and literature.',
    ],
    outcome:
      'lindsaygrimley.ca is the public campaign site. The same pattern works for any time-boxed civic project that has to look finished in a hurry.',
  },
  {
    slug: 'seven-steps-financial',
    path: '/work/seven-steps-financial',
    client: '7 Steps Financial',
    title: 'A planning-practice site for Langley',
    seoTitle: '7 Steps Financial Case Study | Elementary Creative',
    seoDescription:
      'Case study: website for 7 Steps Financial, a financial planning and consulting practice in Langley, BC.',
    excerpt: 'Trust-first layout for a planning practice - process, people, and a quiet CTA.',
    location: 'Langley, BC',
    year: '2026',
    industry: 'Financial planning',
    liveUrl: 'https://7stepsfinancial.com/',
    portfolioName: '7 Steps Financial',
    challenge:
      'A planning practice cannot shout. The site needed to look considered, explain the process, and make it easy to request a conversation.',
    approach: [
      'Structured pages around the practice’s steps instead of a generic “about / services / blog” kit.',
      'Used restrained type and spacing so the work feels like advice, not a lead-gen landing page.',
      'Shipped a production build the team can share with prospective clients.',
    ],
    outcome:
      'The live site is the public face of the Langley practice. We treat it as a client launch in the portfolio, with hosting on their production URL.',
  },
  {
    slug: 'fresh-cut-lawn-bc',
    path: '/work/fresh-cut-lawn-bc',
    client: 'Fresh Cut Lawn BC',
    title: 'A lawn-care site for a Mission, BC crew',
    seoTitle: 'Fresh Cut Lawn BC Case Study | Elementary Creative',
    seoDescription:
      'Case study: website for Fresh Cut Lawn BC, a lawn care and landscaping company in Mission, British Columbia.',
    excerpt: 'Seasonal services and a local brand that reads as a real crew, not a franchise.',
    location: 'Mission, BC',
    year: '2026',
    industry: 'Lawn care & landscaping',
    liveUrl: 'https://freshcutlawnbc.ca/',
    portfolioName: 'Fresh Cut Lawn BC',
    challenge:
      'Lawn-care companies often share the same clip-art look. Fresh Cut needed a site that named the work, the season, and the town.',
    approach: [
      'Lead with services homeowners actually search for - mowing, cleanup, and related landscaping.',
      'Designed for phone-first use; most quotes start on a mobile browser after a driveway visit.',
      'Launched on a production host with a simple path to call or write.',
    ],
    outcome:
      'freshcutlawnbc.ca is live as the public marketing site for the Mission crew. It is also featured on our studio homepage as a recent client launch.',
  },
  {
    slug: 'cardinal-services',
    path: '/work/cardinal-services',
    client: 'Cardinal Services Group',
    title: 'An HVAC company site for Windsor',
    seoTitle: 'Cardinal Services Group Case Study | Elementary Creative',
    seoDescription:
      'Case study: website for Cardinal Services Group, an HVAC company in Windsor, Ontario.',
    excerpt: 'Service list, service area, and a phone number that works from a job site.',
    location: 'Windsor, ON',
    year: '2019',
    industry: 'HVAC',
    liveUrl: 'https://www.cardinalservicesgroup.com',
    portfolioName: 'Cardinal Services Group',
    challenge:
      'A trades company needed a site customers would trust after seeing a van - hours, services, and a way to call, not a brochure of stock furnaces.',
    approach: [
      'Put heating, cooling, and related services in plain language.',
      'Kept contact and the Windsor service area visible on every page.',
      'Shipped a production marketing site the company still uses as its public URL.',
    ],
    outcome:
      'cardinalservicesgroup.com remains the live company site. It is an earlier client launch and still a typical trades brief: prove you are local, then make the phone easy.',
  },
  {
    slug: 'london-top-tailoring',
    path: '/work/london-top-tailoring',
    client: 'London Top Tailoring',
    title: 'A tailor shop site for London, Ontario',
    seoTitle: 'London Top Tailoring Case Study | Elementary Creative',
    seoDescription:
      'Case study: website for London Top Tailoring, a tailoring shop in London, Ontario.',
    excerpt: 'Alterations and custom work explained so a first-time visitor knows what to bring in.',
    location: 'London, ON',
    year: '2017',
    industry: 'Tailoring',
    liveUrl: 'https://www.londontoptailoring.com',
    portfolioName: 'London Top Tailoring',
    challenge:
      'A neighbourhood tailor does not need a fashion lookbook. Customers need hours, services, and enough craft in the design that the shop feels established.',
    approach: [
      'Listed alterations and custom work the way the counter staff describe them.',
      'Used photography of the work instead of generic “suit on a hanger” stock.',
      'Launched a long-running production site the shop still points people to.',
    ],
    outcome:
      'londontoptailoring.com is still the public site. The brief is the ancestor of later studio CRM work (TailorOne): first the storefront, then the back office if they need it.',
  },
  {
    slug: 'cleanex-detailing',
    path: '/work/cleanex-detailing',
    client: 'Cleanex Detailing',
    title: 'Studio and mobile detailing, one site',
    seoTitle: 'Cleanex Detailing Case Study | Elementary Creative',
    seoDescription:
      'Case study: website for Cleanex Detailing, a studio and mobile auto detailing shop in Coquitlam, BC.',
    excerpt: 'Two service modes - shop and mobile - explained without a cluttered menu.',
    location: 'Coquitlam, BC',
    year: '2024',
    industry: 'Auto detailing',
    liveUrl: 'https://www.cleanexdetailing.com/',
    portfolioName: 'Cleanex Detailing - Studio & Mobile',
    challenge:
      'Cleanex runs both a studio and a mobile service. The site had to explain when to come in and when they come to you.',
    approach: [
      'Separated studio vs mobile offers so packages stay scannable.',
      'Used real shop photography instead of generic car-wash stock.',
      'Kept contact and booking obvious on every page.',
    ],
    outcome:
      'cleanexdetailing.com remains the public site for the Coquitlam shop. The project is a typical fit for BC service businesses with more than one fulfillment model.',
  },
  {
    slug: 'ezee-computer-training',
    path: '/work/ezee-computer-training',
    client: 'Ezee Computer Training',
    title: 'A training-school site for London, Ontario',
    seoTitle: 'Ezee Computer Training Case Study | Elementary Creative',
    seoDescription:
      'Case study: website for Ezee Computer Training, a computer training school in London, Ontario.',
    excerpt: 'Programs and contact for a school that sells skills, not software.',
    location: 'London, ON',
    year: '2017',
    industry: 'Computer training',
    liveUrl: 'https://www.ezeecomputertraining.com',
    portfolioName: 'Ezee Computer Training',
    challenge:
      'A training school needs to explain programs to people who may not be technical - and still look like a real institution, not a weekend course flyer.',
    approach: [
      'Organised offerings so a prospective student can pick a path and inquire.',
      'Kept the tone practical; the owner’s testimonial on our studio site came after this work.',
      'Shipped a production marketing site that stayed the school’s public URL.',
    ],
    outcome:
      'ezeecomputertraining.com is the live school site. Jorge Velez later described the redesign as bringing in more contracts - that quote is on our homepage.',
  },
  {
    slug: 'velezfit',
    path: '/work/velezfit',
    client: 'Velezfit',
    title: 'A personal-training site for London, Ontario',
    seoTitle: 'Velezfit Case Study | Elementary Creative',
    seoDescription:
      'Case study: website for Velezfit, a personal training business in London, Ontario.',
    excerpt: 'A trainer’s offer and a way to start - without a gym-chain template.',
    location: 'London, ON',
    year: '2017',
    industry: 'Personal training',
    liveUrl: 'http://www.velezfit.ca',
    portfolioName: 'Velezfit',
    challenge:
      'Independent trainers get lost next to franchise gyms. The site had to feel like one person you can book, not a membership mill.',
    approach: [
      'Led with the trainer and the kind of training on offer.',
      'Made contact the primary action; no fake “transform in 30 days” funnel.',
      'Launched a production site the business still lists publicly.',
    ],
    outcome:
      'velezfit.ca is the live site. The owner’s note on our homepage is about the working relationship, not a traffic screenshot.',
  },
  {
    slug: 'tasklyft',
    path: '/work/tasklyft',
    client: 'TaskLyft',
    title: 'Field-service software for crews that live on a calendar',
    seoTitle: 'TaskLyft Case Study | Elementary Creative',
    seoDescription:
      'Case study: TaskLyft, live field service software for trades - customers, calendar, invoices, and teams.',
    excerpt: 'Customers, jobs, invoices, and a team calendar in one product.',
    location: 'Canada',
    year: '2026',
    industry: 'Field service',
    liveUrl: 'https://tasklyft.ca/',
    portfolioName: 'TaskLyft',
    challenge:
      'Trades teams outgrow a shared inbox and a paper calendar. Off-the-shelf field tools are either too thin or too enterprise.',
    approach: [
      'Built around customers, scheduled work, invoicing, and team visibility.',
      'Designed the admin for a phone in a truck, not only a desktop in the office.',
      'Launched the product on a production domain crews can actually use.',
    ],
    outcome:
      'tasklyft.ca is the live product. Treat it as a studio-built system we can tailor - not a screenshot in a slide deck.',
  },
  {
    slug: 'barberpro',
    path: '/work/barberpro',
    client: 'BarberPro',
    title: 'Chair booking without a maze of widgets',
    seoTitle: 'BarberPro Lab Case Study | Elementary Creative',
    seoDescription:
      'Lab case study: BarberPro, a booking and chair-scheduling product concept for barber shops.',
    excerpt: 'Services, clients, and chair time in one admin - a lab build, not a theme.',
    location: 'Studio lab',
    year: '2026',
    industry: 'Barber booking',
    liveUrl: 'https://barberpro.elementarycreative.com/',
    portfolioName: 'BarberPro',
    lab: true,
    challenge:
      'Most shops bolt a booking widget onto a five-page site. The widget does not know chairs, service length, or who is on the floor.',
    approach: [
      'Modelled services and staff calendars the way a shop actually books a Saturday.',
      'Kept the marketing face thin so the product is the book, not a landing-page kit.',
      'Shipped a public lab URL so prospects can click through the idea.',
    ],
    outcome:
      'BarberPro is a studio prototype at barberpro.elementarycreative.com. A production build would be scoped to a real shop’s chairs and deposit rules.',
  },
  {
    slug: 'leasy',
    path: '/work/leasy',
    client: 'Leasy',
    title: 'Rent, maintenance, and tenants in one place',
    seoTitle: 'Leasy Lab Case Study | Elementary Creative',
    seoDescription:
      'Lab case study: Leasy, a property-management product for rent, maintenance, and tenants.',
    excerpt: 'A landlord/operator tool - not a listing portal dressed up as software.',
    location: 'Studio lab',
    year: '2026',
    industry: 'Property management',
    liveUrl: 'https://tryleasy.ca/',
    portfolioName: 'Leasy',
    lab: true,
    challenge:
      'Small landlords run the building in email and spreadsheets. Consumer rental sites do not replace a maintenance log or a rent ledger.',
    approach: [
      'Scoped the product to rent, work orders, and tenant records.',
      'Kept the UI operator-first: the person paying the bills has to find a ticket in seconds.',
      'Shipped a public lab URL at tryleasy.ca.',
    ],
    outcome:
      'Leasy is a studio product you can open and click. A client build would map to their units, vendors, and how they already collect rent.',
  },
  {
    slug: 'tailorone',
    path: '/work/tailorone',
    client: 'TailorOne',
    title: 'A studio CRM for fittings and production',
    seoTitle: 'TailorOne Lab Case Study | Elementary Creative',
    seoDescription:
      'Lab case study: TailorOne, a studio CRM for clients, orders, fittings, and production tracking.',
    excerpt: 'Client records, orders, and the workroom - one dashboard.',
    location: 'Studio lab',
    year: '2026',
    industry: 'Studio CRM',
    liveUrl: 'https://tailorone.elementarycreative.com/',
    portfolioName: 'TailorOne',
    lab: true,
    challenge:
      'A tailoring studio’s real system is fittings, orders, and who is on the horse. A generic CRM does not know a hem date.',
    approach: [
      'Modelled clients, orders, fittings, and production status as first-class records.',
      'Designed a dashboard a shop manager can use between appointments.',
      'Published a lab URL and featured the product on the studio homepage as a CRM example.',
    ],
    outcome:
      'tailorone.elementarycreative.com is the public prototype. It is the software counterpart to earlier storefront work like London Top Tailoring.',
  },
  {
    slug: 'financial-pro',
    path: '/work/financial-pro',
    client: 'Financial Pro',
    title: 'A financial-services web experience',
    seoTitle: 'Financial Pro Lab Case Study | Elementary Creative',
    seoDescription:
      'Lab case study: Financial Pro, a web experience for financial services.',
    excerpt: 'A calmer product surface for advice-style services - lab, not a live advisory firm.',
    location: 'Studio lab',
    year: '2026',
    industry: 'Financial services',
    liveUrl: 'https://financialpro.elementarycreative.com/',
    portfolioName: 'Financial Pro',
    lab: true,
    challenge:
      'Financial products often look like a bank banner ad. We wanted a reference UI that feels like a practice, not a rate table.',
    approach: [
      'Designed an experience around trust and next steps, not product grids.',
      'Used the same production stack we ship for client marketing sites.',
      'Hosted a public lab URL for walkthroughs.',
    ],
    outcome:
      'financialpro.elementarycreative.com is a studio reference. Client work in this category (see 7 Steps Financial) is scoped to a real practice and compliance needs.',
  },
  {
    slug: 'canwait',
    path: '/work/canwait',
    client: 'CanWait',
    title: 'A waitlist and queue that does not need a clipboard',
    seoTitle: 'CanWait Lab Case Study | Elementary Creative',
    seoDescription:
      'Lab case study: CanWait, a business waitlist and queue-management product.',
    excerpt: 'Walk-in flow for shops that still run the line on paper.',
    location: 'Studio lab',
    year: '2025',
    industry: 'Queue & waitlist',
    liveUrl: 'https://canwait-prod.web.app/',
    portfolioName: 'CanWait',
    lab: true,
    challenge:
      'Cafés, clinics, and counters still manage a line with a pad of paper. Consumer “get in line” apps are built for venues, not a two-person shop.',
    approach: [
      'Kept the guest path short: join, wait, get called.',
      'Gave the counter a queue they can run without a training session.',
      'Deployed a production-style Firebase host so the lab can be demoed on a phone.',
    ],
    outcome:
      'canwait-prod.web.app is the live lab. A client version would match their ticket types and how they already text customers.',
  },
]

export function getCaseStudyBySlug(slug: string | undefined) {
  if (!slug) return undefined
  return caseStudies.find((study) => study.slug === slug)
}

export function getCaseStudyByPortfolioName(name: string) {
  return caseStudies.find((study) => study.portfolioName === name)
}

export function getRelatedCaseStudies(slug: string, limit = 3) {
  const index = caseStudies.findIndex((study) => study.slug === slug)
  if (index === -1) return caseStudies.slice(0, limit)
  const rest = [...caseStudies.slice(index + 1), ...caseStudies.slice(0, index)]
  return rest.slice(0, limit)
}
